import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { Menu, X, Globe, ShieldCheck } from "lucide-react";
import HawkamLogo from "@/components/HawkamLogo";
import { api } from "@/lib/api";
import { useSections, isSectionOn } from "@/hooks/useSections";

export default function Navbar() {
  const { lang, toggle, t } = useI18n();
  const sections = useSections();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [showPackages, setShowPackages] = useState(false);
  const [showNews, setShowNews] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Packages + News links appear only when the admin has published content.
  // Re-check on tab focus so toggling visibility in another tab updates
  // the public navbar without a hard refresh.
  useEffect(() => {
    const refresh = () => {
      api.get("/packages")
        .then((r) => setShowPackages(Array.isArray(r.data) && r.data.length > 0))
        .catch(() => setShowPackages(false));
      api.get("/news")
        .then((r) => setShowNews(Array.isArray(r.data) && r.data.length > 0))
        .catch(() => setShowNews(false));
    };
    refresh();
    const onFocus = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onFocus);
    return () => document.removeEventListener("visibilitychange", onFocus);
  }, [loc.pathname]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/services", label: t("nav.services") },
    { to: "/training", label: t("nav.training") },
    ...(isSectionOn(sections, "campaigns") ? [{ to: "/campaigns", label: t("nav.campaigns") }] : []),
    ...(showPackages && isSectionOn(sections, "packages") ? [{ to: "/packages", label: t("nav.packages") }] : []),
    ...(isSectionOn(sections, "articles") ? [{ to: "/articles", label: t("nav.articles") }] : []),
    ...(showNews && isSectionOn(sections, "news") ? [{ to: "/news", label: t("nav.news") }] : []),
    // Policies link removed from the top navigation per request — still routable at /policies and linked from Footer.
    ...(isSectionOn(sections, "contact") ? [{ to: "/contact", label: t("nav.contact") }] : []),
  ];

  // Solid navy always — never fades to cream/light background even at top.
  const headerBg = scrolled
    ? "bg-navy shadow-[0_2px_20px_rgba(0,0,0,0.35)] border-b border-gold/40"
    : "bg-navy border-b border-gold/20";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${headerBg}`}
    >
      <div className="container-x flex items-center justify-between py-3">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 text-cream group hover:text-gold transition-colors">
          <HawkamLogo size={64} variant="mark" className="drop-shadow-[0_2px_6px_rgba(201,169,97,0.35)] group-hover:scale-105 transition-transform" />
          <span className="flex flex-col leading-tight">
            <span className={`text-2xl tracking-widest ${lang === "ar" ? "font-arabic-serif" : "font-serif"}`}>
              {t("brand")}
            </span>
            <span className="text-[11px] uppercase tracking-[0.3em] text-gold whitespace-nowrap">{t("brandSub")}</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `relative text-[15px] font-medium tracking-wide transition-colors py-1
                 ${isActive
                    ? "text-gold after:content-[''] after:absolute after:-bottom-1 after:left-0 after:right-0 after:h-[2px] after:bg-gold after:rounded"
                    : "text-cream hover:text-gold"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="inline-flex items-center gap-2 text-cream border border-gold/50 px-3 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-gold hover:text-navy transition-colors"
            aria-label="Toggle language"
          >
            <Globe size={14} /> {lang === "ar" ? "EN" : "عربي"}
          </button>
          <a
            href="/admin/login.php"
            data-testid="nav-admin"
            title={lang === "ar" ? "دخول لوحة الإدارة" : "Admin sign in"}
            className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium tracking-wide text-cream/80 hover:text-gold border border-gold/30 hover:border-gold px-3 py-2 rounded-sm transition-colors"
          >
            <ShieldCheck size={13} className="text-gold" />
            {lang === "ar" ? "الإدارة" : "Admin"}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-cream p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-navy border-t border-gold/20 animate-fade-up">
          <div className="container-x py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mnav-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-base font-medium ${isActive ? "text-gold" : "text-cream"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {isSectionOn(sections, "quote") && (
              <Link to="/quote" className="hidden" data-testid="mnav-quote">
                {t("nav.quote")}
              </Link>
            )}
            <a
              href="/admin/login.php"
              data-testid="mnav-admin"
              className="inline-flex items-center gap-2 text-sm text-cream border border-gold/40 px-3 py-2 rounded-sm w-fit hover:bg-gold hover:text-navy transition-colors"
            >
              <ShieldCheck size={14} className="text-gold" />
              {lang === "ar" ? "لوحة الإدارة" : "Admin console"}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
